import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {useCallback} from 'react'

import {
    getProjectDeployedEquipment,
    returnProjectIdentifiedEquipment,
    returnProjectStockEquipment,
    returnProjectThirdPartyEquipment,
} from './api'
import {invalidateEquipmentMoves} from './invalidate-equipment-moves'
import {projectDeployedEquipmentKeys} from './query-keys'
import type {
    IdentifiedEquipmentReturnInput,
    StockEquipmentReturnInput,
    ThirdPartyEquipmentReturnInput,
} from './schemas'

export function useProjectDeployedEquipment(projectId: string) {
    return useQuery({
        queryKey: projectDeployedEquipmentKeys.detail(projectId),
        queryFn: () => getProjectDeployedEquipment(projectId),
    })
}

export function useInvalidateProjectDeployedEquipment() {
    const queryClient = useQueryClient()

    return useCallback(
        (projectId: string | null | undefined) => {
            if (!projectId) {
                return
            }
            void queryClient.invalidateQueries({
                queryKey: projectDeployedEquipmentKeys.detail(projectId),
            })
        },
        [queryClient],
    )
}

export function useReturnProjectStockEquipment(projectId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: StockEquipmentReturnInput) =>
            returnProjectStockEquipment(projectId, input),
        onSuccess: () => invalidateEquipmentMoves(queryClient, projectId),
    })
}

export function useReturnProjectIdentifiedEquipment(projectId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: IdentifiedEquipmentReturnInput) =>
            returnProjectIdentifiedEquipment(projectId, input),
        onSuccess: () => invalidateEquipmentMoves(queryClient, projectId),
    })
}

export function useReturnProjectThirdPartyEquipment(projectId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: ThirdPartyEquipmentReturnInput) =>
            returnProjectThirdPartyEquipment(projectId, input),
        onSuccess: () => invalidateEquipmentMoves(queryClient, projectId),
    })
}
